/**
 * Single source of truth for the auth ACCESS token (+ refresh bootstrap).
 *
 * SECURITY MODEL
 * - PRODUCTION: the SPA is served by Django on the SAME ORIGIN as the API, so
 *   the httpOnly refresh cookie is first-party. The access token therefore lives
 *   ONLY in memory (a module variable) — never written to localStorage — so an
 *   XSS payload cannot read a persisted token or steal one that survives a
 *   reload. On boot and on 401 we mint a fresh access token from the refresh
 *   cookie via /api/token/refresh/.
 * - CROSS-ORIGIN DEV (CRA :3000 -> API :8000): the refresh cookie is cross-site
 *   and won't be sent, which would break reload persistence. There we fall back
 *   to localStorage (dev convenience only). `IN_MEMORY` is true exactly when the
 *   page origin equals the API origin (i.e. production / same-origin).
 *
 * Every caller goes through getAccessToken(); they don't care which mode is on.
 */
const ACCESS_TOKEN_KEY = 'access_token';

function apiOrigin() {
  try {
    return new URL(process.env.REACT_APP_API_URL).origin;
  } catch (_) {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
}

// True in production (front + API same origin) → token kept in memory only.
const IN_MEMORY =
  typeof window !== 'undefined' && window.location.origin === apiOrigin();

let memoryToken = null;

// Post-migration cleanup: in memory mode, drop any access token left in
// localStorage by the earlier (localStorage-backed) stage so nothing lingers.
if (IN_MEMORY) {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch (_) {
    /* ignore */
  }
} else if (typeof window !== 'undefined') {
  // Visibility for the deploy precondition: if production serves the SPA on a
  // different origin/port than REACT_APP_API_URL, the token silently falls back
  // to localStorage (losing the Stage-2 security benefit). Surface that.
  // eslint-disable-next-line no-console
  console.warn(
    '[auth] access token stored in localStorage (page origin != API origin). '
    + 'In production the SPA and API must be same-origin so the token stays '
    + 'in memory only.'
  );
}

let reloadingForAnotherAccount = false;

/** True once this tab is reloading because the session became another account — callers must
 * not treat the missing token as a logout (that would sign the other tab out too). */
export function isReloadingForAnotherAccount() {
  return reloadingForAnotherAccount;
}

/** The account a JWT belongs to (its `user_id` claim), or null. */
export function tokenUserId(token) {
  try {
    const part = String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const id = JSON.parse(atob(part.padEnd(part.length + ((4 - (part.length % 4)) % 4), '='))).user_id;
    return id == null ? null : String(id);
  } catch (_) {
    return null;
  }
}

/** Current access token, or null. */
export function getAccessToken() {
  if (IN_MEMORY) return memoryToken;
  return memoryToken || localStorage.getItem(ACCESS_TOKEN_KEY);
}

/** Store the access token (clears it when given a falsy value). */
export function setAccessToken(token) {
  memoryToken = token || null;
  if (!IN_MEMORY) {
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  }
}

/** Remove the access token (logout / 401). */
export function clearAccessToken() {
  memoryToken = null;
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY); // harmless in memory mode
  } catch (_) {
    /* ignore */
  }
}

/** Convenience Authorization header object ({} when there is no token). */
export function authHeader() {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Mint a fresh access token from the httpOnly refresh cookie.
 * Returns the new token, or null when there is no valid refresh session.
 * Used at app boot (to survive reloads without persisting the token) and by the
 * apiService 401 interceptor (to recover from an expired access token).
 */
export async function refreshAccessToken() {
  let base = '/api';
  try {
    base = `${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}/api`;
  } catch (_) {
    /* keep relative fallback */
  }
  // Hard timeout so a hung refresh can never wedge the boot gate on the loader.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);
  try {
    const res = await fetch(`${base}/token/refresh/`, {
      method: 'POST',
      credentials: 'include', // send the httpOnly refresh cookie
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = await res.json();
    // The refresh cookie is shared by every tab: after a login (or account switch) in another
    // tab it mints a token for THAT account. Carrying on here would act as one person while
    // showing another — reload so the whole tab becomes the account the session now is.
    const before = tokenUserId(memoryToken);
    const after = tokenUserId(data.access);
    if (before && after && before !== after && typeof window !== 'undefined') {
      reloadingForAnotherAccount = true;
      setAccessToken(data.access);
      window.location.reload();
      return null;
    }
    setAccessToken(data.access || null);
    return getAccessToken();
  } catch (_) {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
